import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { generate2FASecret, verify2FASetup } from "services/userApi";

function TwoFactorSetup() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showBackupDialog, setShowBackupDialog] = useState(false);

  const steps = ["Generate Secret", "Scan QR Code", "Verify Setup", "Save Backup Codes"];

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = async () => {
    try {
      const response = await generate2FASecret();
      setSecretKey(response.manual_entry_key);
      setQrCodeUrl(response.qr_code);
    } catch (err) {
      setError("Failed to generate 2FA secret");
    }
  };

  const handleVerifyCode = async () => {
    setError("");
    setLoading(true);
    
    try {
      const response = await verify2FASetup(verificationCode);
      setBackupCodes(response.backup_codes);
      setActiveStep(3);
    } catch (err) {
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setShowBackupDialog(true);
  };

  const handleConfirmBackupSaved = () => {
    // TODO: Call API to confirm 2FA setup completion
    navigate("/settings?page=account");
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ArgonBox textAlign="center" py={3}>
            <ArgonTypography variant="h5" fontWeight="medium" mb={2}>
              Enable Two-Factor Authentication
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text" mb={3}>
              Two-factor authentication adds an extra layer of security to your account.
              You&apos;ll need an authenticator app like Google Authenticator or Authy.
            </ArgonTypography>
            <ArgonButton 
              color="primary" 
              onClick={() => setActiveStep(1)}
            >
              Get Started
            </ArgonButton>
          </ArgonBox>
        );
        
      case 1:
        return (
          <ArgonBox py={3}>
            <ArgonTypography variant="h5" fontWeight="medium" textAlign="center" mb={3}>
              Scan QR Code
            </ArgonTypography>
            
            <ArgonBox display="flex" flexDirection="column" alignItems="center">
              {qrCodeUrl && (
                <ArgonBox mb={3} sx={{ border: "4px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                  <img src={qrCodeUrl} alt="2FA QR Code" style={{ width: 200, height: 200 }} />
                </ArgonBox>
              )}
              
              <ArgonTypography variant="body2" color="text" textAlign="center" mb={2}>
                Scan this QR code with your authenticator app
              </ArgonTypography>
              
              <ArgonBox 
                bgcolor="grey.100" 
                borderRadius={1} 
                p={2} 
                mb={3}
                sx={{ width: "100%", maxWidth: 400 }}
              >
                <ArgonTypography variant="caption" color="text" display="block" mb={1}>
                  Can&apos;t scan? Enter this key manually:
                </ArgonTypography>
                <ArgonTypography 
                  variant="body2" 
                  fontWeight="medium" 
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {secretKey}
                </ArgonTypography>
              </ArgonBox>
              
              <ArgonButton 
                color="primary" 
                onClick={() => setActiveStep(2)}
              >
                Next: Verify Setup
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        );
        
      case 2:
        return (
          <ArgonBox py={3}>
            <ArgonTypography variant="h5" fontWeight="medium" textAlign="center" mb={3}>
              Verify Your Setup
            </ArgonTypography>
            
            <ArgonBox maxWidth={400} mx="auto">
              <ArgonTypography variant="body2" color="text" textAlign="center" mb={3}>
                Enter the 6-digit code from your authenticator app
              </ArgonTypography>
              
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              
              <ArgonBox mb={3}>
                <ArgonInput
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  sx={{
                    "& input": { 
                      textAlign: "center", 
                      fontSize: "24px",
                      letterSpacing: "8px",
                      fontFamily: "monospace"
                    }
                  }}
                />
              </ArgonBox>
              
              <ArgonButton 
                color="primary" 
                fullWidth
                onClick={handleVerifyCode}
                disabled={verificationCode.length !== 6 || loading}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        );
        
      case 3:
        return (
          <ArgonBox py={3}>
            <ArgonTypography variant="h5" fontWeight="medium" textAlign="center" mb={3}>
              Save Your Backup Codes
            </ArgonTypography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <ArgonTypography variant="body2" fontWeight="medium">
                Important: Save these backup codes in a secure location.
              </ArgonTypography>
              <ArgonTypography variant="caption">
                You can use these codes to access your account if you lose your authenticator device.
              </ArgonTypography>
            </Alert>
            
            <ArgonBox 
              bgcolor="grey.100" 
              borderRadius={1} 
              p={3} 
              mb={3}
            >
              {backupCodes.map((code, index) => (
                <ArgonTypography 
                  key={index}
                  variant="body2" 
                  sx={{ fontFamily: "monospace", mb: 1 }}
                >
                  {index + 1}. {code}
                </ArgonTypography>
              ))}
            </ArgonBox>
            
            <ArgonBox display="flex" gap={2} justifyContent="center">
              <ArgonButton 
                variant="outlined"
                color="secondary"
                onClick={() => {
                  const codesText = backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');
                  navigator.clipboard.writeText(codesText);
                }}
              >
                Copy Codes
              </ArgonButton>
              <ArgonButton 
                color="primary"
                onClick={handleComplete}
              >
                I&apos;ve Saved My Codes
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        );
        
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox maxWidth={600} mx="auto">
          <Card>
            <ArgonBox p={3}>
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              
              {renderStepContent()}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      
      {/* Backup Codes Confirmation Dialog */}
      <Dialog open={showBackupDialog} onClose={() => setShowBackupDialog(false)}>
        <DialogContent>
          <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
            Confirm Backup Codes Saved
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            Please confirm that you have saved your backup codes in a secure location. 
            You won&apos;t be able to see these codes again.
          </ArgonTypography>
        </DialogContent>
        <DialogActions>
          <ArgonButton 
            color="secondary" 
            onClick={() => setShowBackupDialog(false)}
          >
            Go Back
          </ArgonButton>
          <ArgonButton 
            color="primary" 
            onClick={handleConfirmBackupSaved}
          >
            Yes, I&apos;ve Saved Them
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default TwoFactorSetup;